/**
 * Which sections and fields an item's form shows (docs/studio-console-redesign.md, section 4):
 * the form asks only what this kind of item needs, in the business's words. A yoga class asks for
 * its instructor, level and equipment and gets a Moves section; a recipe asks for servings and
 * times and gets a Recipe section; a film asks for its year and director. Sections that do not
 * apply to the kind are absent, and rarely used fields wait in More (collapsed): never more than
 * two levels of disclosure.
 *
 * Pure: data in, a plan out (no React, no imports at run time), so it is unit-tested and the
 * section components stay dumb. Every field names its path in the item's document, which is also
 * how a problem from Review & publish (`field`) finds the section and the field to open.
 */

import type { EntityType } from "../api/admin";
import type { Pack } from "../vocabulary/vocabulary";

export interface Option { value: string; label: string }

export type Control =
  | { kind: "text"; multiline?: boolean; placeholder?: string }
  /** Text in the brand's languages: the default language first, the others behind "Other languages". */
  | { kind: "localized"; multiline?: boolean }
  | { kind: "number"; min?: number; step?: number; unit?: string; integer?: boolean }
  | { kind: "select"; options: Option[]; allowNone?: boolean }
  | { kind: "year" }
  | { kind: "date" }
  | { kind: "datetime" }
  | { kind: "boolean" }
  /** The people credited in one role (credits[] with that role): picker cards. */
  | { kind: "people"; role: string }
  /** Credits in every role the Basics do not ask for. */
  | { kind: "otherCredits"; skipRoles: string[] }
  /** One side of a match (participants[] with that side). */
  | { kind: "team"; side: "home" | "away" }
  /** One item of another type, as a picker card (a session's stage). */
  | { kind: "ref"; types: EntityType[] }
  | { kind: "ageBands" }
  | { kind: "kids" }
  /** Category picker: the brand's terms, grouped by list. */
  | { kind: "terms" }
  | { kind: "tags" }
  | { kind: "links" }
  | { kind: "attribution" }
  | { kind: "availability" }
  | { kind: "rating" }
  | { kind: "timeZone" }
  /** Shown, never edited: the item's id, its ids elsewhere. */
  | { kind: "readonlyId" }
  | { kind: "externalIds" };

export interface FieldPlan {
  path: string;
  label: string;
  help?: string;
  required?: boolean;
  control: Control;
}

export interface ArtworkRole {
  /** The image role (media/domain/images.ts): it sets the crop and the sizes made. */
  role: "poster" | "backdrop" | "thumbnail" | "square" | "logo" | "hero" | "topShelf" | "person";
  /** Where the image sits in the document. */
  path: string;
  label: string;
  /** The shape in words: "2:3, tall". */
  shape: string;
  /** Width ÷ height for the drawn frame; null keeps the image's own (logos). */
  aspect: number | null;
  use: string;
}

export interface MomentKind { kind: string; word: string }

export type SectionPlan =
  | { id: "basics"; title: string; fields: FieldPlan[] }
  | { id: "video"; title: string; intro: string; trailer: boolean }
  | { id: "streams"; title: string; intro: string; feedKind: "angle" | "stage"; feedWord: string }
  | { id: "moments"; title: string; intro: string; path: string; kinds: MomentKind[]; rounds: boolean; score: boolean; timer: boolean }
  | { id: "recipe"; title: string; intro: string }
  | { id: "parts"; title: string; intro: string; partOne: string; partMany: string; itemOne: string; itemMany: string; numbering: "per-part" | "continuous" }
  | { id: "items"; title: string; intro: string }
  | { id: "terms"; title: string; intro: string }
  | { id: "artwork"; title: string; intro: string; primary: ArtworkRole[]; more: ArtworkRole[] }
  | { id: "access"; title: string; intro: string }
  | { id: "more"; title: string; intro: string; fields: FieldPlan[] };

export type SectionId = SectionPlan["id"];

export interface PlanInput {
  type: EntityType;
  /** The item's own kind (a title's film/class/recipe…, a series' series/course/programme). */
  kind?: string;
  pack: Pack;
  /** The brand has stages (or its business shows them): sessions name theirs. */
  stages: boolean;
  /** The brand's apps offer downloads (manifest features.downloads). */
  downloads: boolean;
  /** The brand's apps have kids profiles (manifest features.kidsMode). */
  kids: boolean;
}

export interface FormPlan {
  sections: SectionPlan[];
}

// ---- words ----

export const TITLE_KINDS: Option[] = [
  { value: "film", label: "Film" }, { value: "episode", label: "Episode" }, { value: "class", label: "Class" },
  { value: "lesson", label: "Lesson" }, { value: "clip", label: "Clip" }, { value: "recording", label: "Recording" },
  { value: "recipe", label: "Recipe" }, { value: "sermon", label: "Message" }, { value: "match", label: "Match" },
  { value: "concert", label: "Concert" }, { value: "performance", label: "Performance" }, { value: "session", label: "Session" },
  { value: "short", label: "Short" },
];

export const SERIES_KINDS: Option[] = [
  { value: "series", label: "Series: seasons of episodes" },
  { value: "course", label: "Course: modules of lessons" },
  { value: "programme", label: "Programme: a day-by-day plan" },
];

export const LAYOUTS: Option[] = [
  { value: "row", label: "Wide cards (16:9)" },
  { value: "poster-row", label: "Posters (2:3, tall)" },
  { value: "editorial", label: "Large cards with words" },
  { value: "square-row", label: "Squares" },
  { value: "numbered", label: "Top 10, numbered" },
  { value: "grid", label: "A grid of posters" },
  { value: "hero", label: "One big banner" },
];

const LEVELS: Option[] = [
  { value: "beginner", label: "Beginner" }, { value: "intermediate", label: "Intermediate" },
  { value: "advanced", label: "Advanced" }, { value: "all", label: "All levels" },
];

const ROLE_SPECS: Record<ArtworkRole["role"], Omit<ArtworkRole, "role" | "path">> = {
  poster: { label: "Poster", shape: "2:3, tall", aspect: 2 / 3, use: "Rows of posters and the detail page." },
  backdrop: { label: "Backdrop", shape: "16:9, wide", aspect: 16 / 9, use: "The big picture at the top of a screen and behind the details." },
  thumbnail: { label: "Thumbnail", shape: "16:9, small", aspect: 16 / 9, use: "Rows of wide cards and Continue watching. Made from the backdrop when missing." },
  square: { label: "Square", shape: "1:1", aspect: 1, use: "Square rows and phone lists." },
  logo: { label: "Title logo", shape: "Transparent, any shape", aspect: null, use: "Drawn over the backdrop instead of the name." },
  hero: { label: "Banner", shape: "12:5, very wide", aspect: 12 / 5, use: "A wide banner at the top of a screen or a collection." },
  topShelf: { label: "Apple TV Top Shelf", shape: "2320 × 720", aspect: 2320 / 720, use: "The Apple TV home screen and the Android TV launcher." },
  person: { label: "Photo", shape: "1:1", aspect: 1, use: "Next to the name wherever this person is credited." },
};

const art = (role: ArtworkRole["role"], path = `artwork.${role}`): ArtworkRole => ({ role, path, ...ROLE_SPECS[role] });

// ---- field builders ----

const name = (label = "Name"): FieldPlan => ({ path: "title", label, required: true, control: { kind: "localized" } });
const synopsis: FieldPlan = { path: "synopsis", label: "Short description", help: "One or two sentences, shown on the card and at the top of the details.", control: { kind: "localized", multiline: true } };
const people = (role: string, label: string, help?: string): FieldPlan => ({ path: `credits:${role}`, label, help, control: { kind: "people", role } });
const level: FieldPlan = { path: "attributes.level", label: "Level", control: { kind: "select", options: LEVELS, allowNone: true } };
const equipment: FieldPlan = { path: "attributes.equipment", label: "Equipment", help: "What viewers need, e.g. a mat and light weights. Write “none” when nothing is needed.", control: { kind: "text" } };
const date = (label: string): FieldPlan => ({ path: "releasedAt", label, control: { kind: "date" } });
const year: FieldPlan = { path: "releasedAt", label: "Year", help: "The year it came out.", control: { kind: "year" } };
const attr = (key: string, label: string, help?: string): FieldPlan => ({ path: `attributes.${key}`, label, help, control: { kind: "text" } });
const ageBands: FieldPlan = { path: "audience.ageBands", label: "Ages", help: "Kids profiles of these ages see it.", control: { kind: "ageBands" } };

/** The person a business credits first on its items, in its own words (Instructor, Speaker…). */
function mainRole(pack: Pack): { role: string; label: string } | null {
  switch (pack.vertical) {
    case "fitness": return { role: "instructor", label: "Instructor" };
    case "faith": return { role: "speaker", label: "Speaker" };
    case "education": return { role: "teacher", label: "Teacher" };
    case "music": return { role: "artist", label: "Artist" };
    case "food": return { role: "chef", label: "Chef" };
    case "events": return { role: "speaker", label: "Speaker" };
    default: return null;
  }
}

/** The key facts of each kind of title: what a viewer asks first about it. */
function titleFacts(kind: string, pack: Pack): FieldPlan[] {
  switch (kind) {
    case "class": return [people("instructor", "Instructor"), level, equipment];
    case "film": return [year, people("director", "Director")];
    case "sermon": return [people("speaker", "Speaker"), date("Date"), attr("passage", "Bible passage", "e.g. John 3:16–21")];
    case "match": return [date("Played on"), attr("homeTeam", "Home team"), attr("awayTeam", "Away team"), attr("score", "Final score", "e.g. 2–1")];
    case "lesson": return [people("teacher", "Teacher"), level];
    case "concert": return [people("artist", "Artist"), date("Date"), attr("venue", "Venue")];
    case "recording": return [people("artist", "Artist"), date("Date")];
    case "recipe": return [
      people("chef", "Chef"),
      { path: "recipe.servings", label: "Servings", help: "Viewers can scale the ingredients from this.", control: { kind: "number", min: 1, integer: true } },
      { path: "recipe.prepMinutes", label: "Preparation", control: { kind: "number", min: 0, integer: true, unit: "min" } },
      { path: "recipe.cookMinutes", label: "Cooking", control: { kind: "number", min: 0, integer: true, unit: "min" } },
    ];
    case "performance": return [people("director", "Director"), attr("company", "Company"), attr("venue", "Venue"), date("Date")];
    case "session": return [people("speaker", "Speaker"), date("Date")];
    case "episode": return pack.vertical === "kids" ? [] : [date("First shown")];
    default: return [date("Date")];
  }
}

/** The moments a kind of title has on its video, and what the section calls them. */
function titleMoments(kind: string): Omit<Extract<SectionPlan, { id: "moments" }>, "id" | "path"> | null {
  const base = { rounds: false, score: false, timer: false };
  switch (kind) {
    case "class": return { ...base, title: "Moves", intro: "The moves of the class in order. The apps show each move's name, its round (3 of 7) and its timer.", kinds: [{ kind: "move", word: "Move" }], rounds: true, timer: true };
    case "concert": case "recording": return { ...base, title: "Setlist", intro: "The songs, where each starts. Viewers jump to a song from the player.", kinds: [{ kind: "song", word: "Song" }] };
    case "performance": return { ...base, title: "Acts", intro: "The acts and the interval. The player shows where each starts.", kinds: [{ kind: "act", word: "Act" }, { kind: "interval", word: "Interval" }] };
    case "match": return { ...base, title: "Key moments", intro: "Goals, cards and changes, with the score after each.", kinds: [{ kind: "key-moment", word: "Key moment" }], score: true };
    case "recipe": return null;
    case "lesson": return { ...base, title: "Chapters and notes", intro: "Chapters to jump to, and notes the player shows at a moment.", kinds: [{ kind: "chapter", word: "Chapter" }, { kind: "note", word: "Note" }] };
    default: return { ...base, title: "Chapters", intro: "Named parts of the video. Viewers jump to them from the player.", kinds: [{ kind: "chapter", word: "Chapter" }] };
  }
}

/** A live event's timeline, by business: goals for a match, songs for a concert, parts of a service. */
function liveMoments(pack: Pack): Omit<Extract<SectionPlan, { id: "moments" }>, "id" | "path"> {
  const base = { rounds: false, score: false, timer: false, intro: "Moments from the start of the event. Live, the apps reveal each as the clock passes it." };
  switch (pack.vertical) {
    case "sport": return { ...base, title: "Key moments", kinds: [{ kind: "key-moment", word: "Key moment" }], score: true };
    case "music": return { ...base, title: "Setlist", kinds: [{ kind: "song", word: "Song" }] };
    case "theatre": return { ...base, title: "Acts", kinds: [{ kind: "act", word: "Act" }, { kind: "interval", word: "Interval" }] };
    case "faith": return { ...base, title: "Order of service", kinds: [{ kind: "service-part", word: "Part of the service" }] };
    case "fitness": return { ...base, title: "Moves", kinds: [{ kind: "move", word: "Move" }], rounds: true, timer: true };
    default: return { ...base, title: "Timeline", kinds: [{ kind: "chapter", word: "Chapter" }, { kind: "note", word: "Note" }] };
  }
}

const access: SectionPlan = { id: "access", title: "Who can watch", intro: "Free for everyone, for members, or for people who rent or buy it. Prices come from what you sell (Pricing & access)." };

function more(fields: Array<FieldPlan | false | null | undefined>): SectionPlan {
  return { id: "more", title: "More", intro: "Description, categories, credits, dates and the rest.", fields: fields.filter(Boolean) as FieldPlan[] };
}

const description: FieldPlan = { path: "description", label: "Long description", help: "For the details page, under the short description.", control: { kind: "localized", multiline: true } };
const terms = (pack: Pack): FieldPlan => ({ path: "terms", label: pack.nouns.taxonomy.many, help: "What viewers can browse and filter by.", control: { kind: "terms" } });
const tags: FieldPlan = { path: "tags", label: "Search words", help: "Words people may search for that are not in the name. Separate with commas.", control: { kind: "tags" } };
const availability: FieldPlan = { path: "availability", label: "When and where it shows", help: "Leave empty to show it always, everywhere.", control: { kind: "availability" } };
const rating: FieldPlan = { path: "rating", label: "Age rating", control: { kind: "rating" } };
const links: FieldPlan = { path: "links", label: "Links", help: "Somewhere outside the app (giving, tickets, a shop). TVs show them as a QR code.", control: { kind: "links" } };
const attribution: FieldPlan = { path: "attribution", label: "Licence credit", help: "The credit line a licence such as CC BY asks for.", control: { kind: "attribution" } };
const externalIds: FieldPlan = { path: "externalIds", label: "Ids elsewhere", control: { kind: "externalIds" } };
const itemId: FieldPlan = { path: "id", label: "Link name", help: "Set from the name when it was created. Apps and links use it, so it never changes.", control: { kind: "readonlyId" } };
const kids: FieldPlan = { path: "audience.kids", label: "For kids profiles", control: { kind: "kids" } };

// ---- the plan ----

export function formPlan(input: PlanInput): FormPlan {
  const { type, pack } = input;
  const kind = input.kind ?? "";
  const isKids = pack.vertical === "kids";
  const audience = isKids ? [] : input.kids ? [kids, ageBands] : [];
  const main = mainRole(pack);

  switch (type) {
    case "title": {
      const facts = titleFacts(kind, pack);
      const factPaths = new Set(facts.map((f) => f.path));
      const creditRoles = facts.filter((f) => f.control.kind === "people").map((f) => (f.control as { role: string }).role);
      const moments = titleMoments(kind);
      return {
        sections: [
          { id: "basics", title: "Basics", fields: [name(), ...facts, ...(isKids ? [ageBands] : []), synopsis] },
          { id: "video", title: "Video", intro: "Upload the video or choose one you uploaded. Its length, captions and scrub previews come from the file.", trailer: true },
          ...(kind === "recipe" ? [{ id: "recipe", title: "Recipe", intro: "Ingredients and steps. Cook mode shows one step at a time and loops the step's clip from the video." } as SectionPlan] : []),
          ...(moments ? [{ id: "moments", path: "playback.markers", ...moments } as SectionPlan] : []),
          { id: "artwork", title: "Artwork", intro: "Upload a picture for each shape, or choose one you uploaded. Each upload is cropped to the shape around the point you pick.", primary: kind === "recipe" || pack.vertical === "food" ? [art("square"), art("backdrop"), art("poster")] : [art("poster"), art("backdrop"), art("thumbnail")], more: [art("logo"), ...(kind === "recipe" || pack.vertical === "food" ? [art("thumbnail")] : [art("square")]), art("hero"), art("topShelf")] },
          access,
          more([
            description, terms(pack), tags,
            !factPaths.has("releasedAt") && { path: "releasedAt", label: "Released", control: { kind: "date" } },
            { path: "credits", label: "Other credits", help: "Everyone else credited, with their role.", control: { kind: "otherCredits", skipRoles: creditRoles } },
            ...audience, rating,
            input.downloads && { path: "downloadable", label: "Viewers may download it", help: "To watch offline on a phone or tablet.", control: { kind: "boolean" } },
            availability, links, attribution, externalIds, itemId,
            { path: "kind", label: "Kind", help: "Sets which facts the form asks for.", control: { kind: "select", options: TITLE_KINDS } },
          ]),
        ],
      };
    }

    case "series": {
      const facts: FieldPlan[] =
        kind === "programme" ? [people(main?.role ?? "instructor", main?.label ?? "Instructor"), level, equipment, { path: "attributes.unlockInOrder", label: "Unlock days in order", help: "Each day opens once the one before is done.", control: { kind: "boolean" } }]
        : kind === "course" ? [people("teacher", "Teacher"), level]
        : main ? [people(main.role, main.label)] : [];
      const words = kind === "programme"
        ? { partOne: "Week", partMany: "Weeks", itemOne: "Day", itemMany: "Days", numbering: "continuous" as const }
        : kind === "course"
          ? { partOne: "Module", partMany: "Modules", itemOne: pack.nouns.title.one, itemMany: pack.nouns.title.many, numbering: "per-part" as const }
          : { partOne: "Season", partMany: "Seasons", itemOne: pack.nouns.title.one, itemMany: pack.nouns.title.many, numbering: "per-part" as const };
      const creditRoles = facts.filter((f) => f.control.kind === "people").map((f) => (f.control as { role: string }).role);
      return {
        sections: [
          { id: "basics", title: "Basics", fields: [name(), ...facts, ...(isKids ? [ageBands] : []), synopsis] },
          { id: "parts", title: words.partMany, intro: kind === "programme" ? `The days in order. Viewers follow one day at a time; the apps number them from 1.` : `The ${words.partMany.toLowerCase()} in order, each with its ${words.itemMany.toLowerCase()}. The apps number them as they are listed here.`, ...words },
          { id: "artwork", title: "Artwork", intro: "Upload a picture for each shape, or choose one you uploaded.", primary: [art("poster"), art("backdrop"), art("thumbnail")], more: [art("logo"), art("square"), art("hero"), art("topShelf")] },
          access,
          more([description, terms(pack), tags, { path: "credits", label: "Other credits", control: { kind: "otherCredits", skipRoles: creditRoles } }, ...audience, rating, availability, links, attribution, externalIds, itemId,
            { path: "kind", label: "Kind", help: "How its parts are counted and shown.", control: { kind: "select", options: SERIES_KINDS } }]),
        ],
      };
    }

    case "liveEvent": {
      const sport = pack.vertical === "sport";
      const facts: FieldPlan[] = [
        name(),
        { path: "startsAt", label: "Starts", required: true, control: { kind: "datetime" } },
        { path: "endsAt", label: "Ends", help: "Leave empty when it is open-ended.", control: { kind: "datetime" } },
        ...(sport ? [{ path: "participants:home", label: "Home team", control: { kind: "team", side: "home" } } as FieldPlan, { path: "participants:away", label: "Away team", control: { kind: "team", side: "away" } } as FieldPlan] : []),
        ...(input.stages ? [{ path: "stageId", label: "Stage", help: "Where it takes place, for the schedule.", control: { kind: "ref", types: ["stage"] } } as FieldPlan] : []),
        ...(main ? [people(main.role, main.label)] : []),
        synopsis,
      ];
      const stageFeeds = input.stages || pack.vertical === "music" || pack.vertical === "theatre";
      return {
        sections: [
          { id: "basics", title: "Basics", fields: facts },
          { id: "streams", title: "Video", intro: "The live stream, or a recording played as if live from the start time, and the replay afterwards.", feedKind: stageFeeds ? "stage" : "angle", feedWord: stageFeeds ? "Stage" : "Camera angle" },
          { id: "moments", path: "timeline", ...liveMoments(pack) },
          { id: "artwork", title: "Artwork", intro: "Upload a picture for each shape, or choose one you uploaded.", primary: [art("backdrop"), art("poster"), art("thumbnail")], more: [art("logo"), art("square"), art("hero"), art("topShelf")] },
          access,
          more([description, terms(pack), tags,
            { path: "doorsOpenAt", label: "Pre-show opens", help: "When the countdown and programme notes appear.", control: { kind: "datetime" } },
            { path: "timeZone", label: "Time zone it is announced in", control: { kind: "timeZone" } },
            { path: "credits", label: "Other credits", control: { kind: "otherCredits", skipRoles: main ? [main.role] : [] } },
            ...audience, availability, links, attribution, externalIds, itemId]),
        ],
      };
    }

    case "collection":
      return {
        sections: [
          { id: "basics", title: "Basics", fields: [name(), { path: "layout", label: "Shape in the app", help: "How its row looks on Home and other screens.", required: true, control: { kind: "select", options: LAYOUTS } }] },
          { id: "items", title: "In this collection", intro: "In the order viewers see them." },
          { id: "artwork", title: "Artwork", intro: "Shown when the collection itself is a card or opens as a page.", primary: [art("backdrop"), art("hero")], more: [art("poster"), art("thumbnail"), art("square")] },
          more([
            { path: "note", label: "Curator's note", help: "A few words on the collection page.", control: { kind: "localized", multiline: true } },
            { path: "curator", label: "Chosen by", help: "Credited on the collection page.", control: { kind: "text" } },
            availability, ...(input.kids || isKids ? [kids, ageBands] : []), itemId,
          ]),
        ],
      };

    case "person":
      return {
        sections: [
          { id: "basics", title: "Basics", fields: [{ path: "name", label: "Name", required: true, control: { kind: "text" } }, { path: "bio", label: "About", control: { kind: "localized", multiline: true } }] },
          { id: "artwork", title: "Photo", intro: "Shown next to the name wherever this person is credited.", primary: [art("person", "image")], more: [] },
          more([itemId]),
        ],
      };

    case "taxonomy":
      return {
        sections: [
          { id: "basics", title: "Basics", fields: [name("Name of the list")] },
          { id: "terms", title: `In this list`, intro: "What viewers browse and filter by. Names can change; the apps keep track." },
          more([itemId]),
        ],
      };

    case "stage":
      return {
        sections: [
          { id: "basics", title: "Basics", fields: [name()] },
          { id: "artwork", title: "Artwork", intro: "Shown on the schedule.", primary: [art("backdrop"), art("thumbnail")], more: [art("logo")] },
          more([itemId]),
        ],
      };

    case "page":
      return { sections: [{ id: "basics", title: "Basics", fields: [name("Name of the screen")] }, more([itemId])] };
  }
}

/** The paths each section answers for, so a problem's `field` finds its section (the longest match wins). */
export function sectionPaths(section: SectionPlan): string[] {
  switch (section.id) {
    case "basics": return section.fields.flatMap((f) => fieldPaths(f));
    case "more": return section.fields.flatMap((f) => fieldPaths(f));
    case "video": return ["playback", "durationSeconds"];
    case "streams": return ["live", "replay", "feeds", "liveMode", "replayDays"];
    case "moments": return [section.path];
    case "recipe": return ["recipe"];
    case "parts": return ["seasons"];
    case "items": return ["itemIds"];
    case "terms": return ["terms"];
    case "artwork": return [...new Set(["artwork", ...section.primary.map((r) => r.path), ...section.more.map((r) => r.path)])];
    case "access": return ["entitlement"];
  }
}

/** A field's own paths: "credits:director" is kept in credits, "participants:home" in participants. */
function fieldPaths(f: FieldPlan): string[] {
  const [base] = f.path.split(":");
  return f.path.includes(":") ? [f.path, base!] : [f.path];
}

/** The section that holds a path of the document (e.g. "playback.video.videoId" → video). */
export function sectionForPath(plan: FormPlan, path: string): SectionId {
  let best: { id: SectionId; length: number } | null = null;
  for (const section of plan.sections) {
    for (const owned of sectionPaths(section)) {
      const match = path === owned || path.startsWith(`${owned}.`);
      if (match && (!best || owned.length > best.length)) best = { id: section.id, length: owned.length };
    }
  }
  return best?.id ?? (plan.sections.some((s) => s.id === "more") ? "more" : plan.sections[0]!.id);
}

/** The field a path belongs to inside its section (the field whose path is the longest prefix), if any. */
export function fieldForPath(plan: FormPlan, path: string): FieldPlan | undefined {
  let best: FieldPlan | undefined;
  for (const section of plan.sections) {
    if (section.id !== "basics" && section.id !== "more") continue;
    for (const f of section.fields) {
      for (const owned of fieldPaths(f)) {
        if ((path === owned || path.startsWith(`${owned}.`)) && (!best || owned.length > best.path.length)) best = f;
      }
    }
  }
  return best;
}
